<template>
    <div class="LeagueFilter filterContent">
        <div class="LeagueFilter-header">
            <div class="JARVIS_Filter_Btn_Close" v-on:click="onClose">
                <SvgIcon iconName="systemIcon_close" />
            </div>
            <div class="LeagueFilter-header-title">
                {{ i18n.t("sportsPage_filterTitle") }}
            </div>
            <div class="LeagueFilter-clearAll JARVIS_Filter_Btn_ClearAll" v-on:click="onClearAll">
                {{ i18n.t('sportsPage_filterClearAll') }}
            </div>
        </div>
        <div class="LeagueFilter-content">
            <div v-if="isShowDateFilter" class="dateFilter">
                <div class="dateFilter-title">
                    {{ i18n.t(isEarlyMarket ? 'sportsPage_filterDate' : 'sportsPage_filterTime') }}
                </div>
                <div v-if="isEarlyMarket" class="dateFilter-content">
                    <div
                        v-for="(date, index) in dateFilters"
                        v-bind:key="`date-${index}`"
                        class="dateFilter-content-listItem"
                        v-bind:class="[
                            { _selected: index === dateFilterIndex },
                            dayTrackingClass(index),
                            { _disabled: !isDateFilterEnabled(dateFilterValues[index]) },
                        ]"
                        v-on:click="changeDateFilter(dateFilterValues[index], index, true)"
                    >
                        <div v-if="index === 0">
                            {{ i18n.t('sportsPage_filter_all') }}
                        </div>
                        <div v-else-if="index === 4">
                            {{ i18n.t('sportsPage_filter_laterDates') }}
                        </div>
                        <div v-else>
                            {{ date }}
                        </div>

                        <div v-if="days[index]">
                            {{ i18n.t(`sportsPage_filterDate_day${days[index]}`) }}
                        </div>
                    </div>
                </div>
                <div v-else class="dateFilter-content">
                    <div
                        v-for="(time, index) in timeFilters"
                        v-bind:key="`time-${time}`"
                        class="dateFilter-content-listItem"
                        v-bind:class="[
                            { _selected: index === dateFilterIndex },
                            timeTrackingClass(time),
                            { _disabled: !isTimeFilterEnabled(time) },
                        ]"
                        v-on:click="changeTimeFilter(time, index, true)"
                    >
                        {{ i18n.t(time === 'All' ? 'sportsPage_filter_all' : `sportsPage_filterTime_next${time}Hrs`) }}
                    </div>
                </div>
            </div>
            <div class="LeagueFilter-content-title">
                {{ i18n.t("sportsPage_filterLeague") }}
            </div>
            <LeagueFilterDetail
                v-for="league in leagues"
                v-bind:key="league.leagueId"
                v-bind:league="league"
                v-bind:selected="selectedLeagues.includes(league.leagueId)"
                class="LeagueFilter-listItem"
                v-on:click="clickLeague(league.leagueId)"
            />
        </div>
        <div class="LeagueFilter-footer">
            <button
                type="button"
                class="button LeagueFilter-footer-btn JARVIS_Filter_Btn_Submit"
                v-on:click="onSubmit"
            >
                {{ i18n.t('sportsPage_filterbtn') }}
            </button>
        </div>
    </div>
</template>

<script lang="ts">
    import orderBy from 'lodash/orderBy';
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onUnmounted, ref } from 'vue';
    import LeagueFilterDetail from '@/components/LeagueFilterDetail.vue';
    import type { ILeague, ILeagueId, ILeagueMatchList } from '@/interface/ILeagueMatchList';
    import type { IMatch } from '@/interface/IMatch';
    import { MarketPage } from '@/interface/enum';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { addDay, getDateAfterTodayWithMonth, getNowWithGMT } from '@/libraries/utils/date';
    import { disableScrolling, enableScrolling } from '@/libraries/utils/dom';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    export default defineComponent({
        name: 'LeagueFilter',
        components: {
            LeagueFilterDetail,
            SvgIcon,
        },
        emits: ['onClose'],
        props: {
            leagueEvents: {
                type: Array as PropType<ILeagueMatchList[][]>,
                default: () => [],
            },
        },
        setup(props, context) {
            disableScrolling();
            onUnmounted(() => enableScrolling());
            const { marketPage, filter } = storeToRefs(useRootStore());

            const isEarlyMarket = computed(() => marketPage.value === MarketPage.EarlyMarket);
            const isToday = computed(() => marketPage.value === MarketPage.Today);
            const isShowDateFilter = computed(() => isEarlyMarket.value || isToday.value);

            const selectedDate = ref<string | Date | null>(null);
            const getSelectedDate = computed(() => (selectedDate.value && selectedDate.value instanceof Date ? new Date(selectedDate.value.toDateString()) : null));

            const selectedHour = ref<number | null>(null);
            const getStartDateTime = computed(() => {
                if (isToday.value && selectedHour.value) {
                    return new Date(getNowWithGMT(8).toDateString());
                }

                if (isEarlyMarket.value && selectedDate.value) {
                    return getSelectedDate.value;
                }
                return null;
            });

            const isSelectedLater = ref(false);
            const getEndDateTime = computed(() => {
                if (isToday.value && selectedHour.value) {
                    const now = getNowWithGMT(8);
                    now.setHours(now.getHours() + selectedHour.value);
                    return now;
                }

                if (isEarlyMarket.value && selectedDate.value && !isSelectedLater.value && getSelectedDate.value) {
                    const startDate = new Date(getSelectedDate.value);
                    startDate.setDate(startDate.getDate() + 1);
                    return startDate;
                }

                return null;
            });

            const leagues = computed(() => {
                const sportsLeagues = props.leagueEvents.reduce<ILeague[]>((acc, curr) => {
                    const filterEvent = curr.filter((event) => {
                        if (event.type === 'outright') {
                            return true;
                        }

                        return (
                            !getStartDateTime.value || new Date(event.showTime).getTime() >= getStartDateTime.value.getTime())
                            && (!getEndDateTime.value || new Date(event.showTime).getTime() < getEndDateTime.value.getTime());
                    });
                    if (filterEvent.length > 0) {
                        const leagueId = filterEvent[0].type === 'outright' ? filterEvent[0].id : filterEvent[0].leagueId;
                        if (!acc.some(x => x.leagueId === leagueId)) {
                            acc.push({
                                leagueId,
                                leagueName: getTranslated(filterEvent[0].leagueName),
                            });
                        }
                    }
                    return acc;
                }, []);
                return orderBy(sportsLeagues, x => x.leagueName);
            });

            const selectedLeagues = ref<ILeagueId[]>([]);
            selectedLeagues.value = filter.value ? [...filter.value.leagueIds] : [];

            function onClose() {
                context.emit('onClose');
            }

            const dateFilterIndex = ref(0);
            function onClearAll() {
                selectedLeagues.value = [];
                if (isShowDateFilter.value) {
                    dateFilterIndex.value = 0;
                    isSelectedLater.value = false;
                    selectedHour.value = null;
                    selectedDate.value = null;
                }
            }

            const { setFilter } = useRootStore();
            function onSubmit() {
                setFilter({
                    leagueIds: selectedLeagues.value.map(x => Number(x)),
                    startDateTime: getStartDateTime.value,
                    endDateTime: getEndDateTime.value,
                    dateFilterIndex: getStartDateTime.value ? dateFilterIndex.value : 0,
                });
                context.emit('onClose');
            }

            function clickLeague(leagueId: ILeagueId) {
                if (selectedLeagues.value.includes(leagueId)) {
                    selectedLeagues.value = selectedLeagues.value.filter(x => x !== leagueId);
                } else {
                    selectedLeagues.value.push(leagueId);
                }
            }

            const dateFilters = [
                'All',
                getDateAfterTodayWithMonth(1, -4),
                getDateAfterTodayWithMonth(2, -4),
                getDateAfterTodayWithMonth(3, -4),
                'Later',
            ];

            const _days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const today = getNowWithGMT(-4);
            const getDayAfter = (offset: number) => _days[addDay(today, offset).getDay()];
            const days = [
                '',
                getDayAfter(1),
                getDayAfter(2),
                getDayAfter(3),
                '',
            ];

            type DateFilterValue = 'All' | 'Later' | Date;
            const dateFilterValues: DateFilterValue[] = [
                'All',
                addDay(today, 1),
                addDay(today, 2),
                addDay(today, 3),
                'Later',
            ];

            type TimeFilter = 'All' | number;
            const timeFilters: TimeFilter[] = ['All', 3, 6, 9, 12];

            function changeDateFilter(time: DateFilterValue, index: number, clearLeague: boolean) {
                if (!isDateFilterEnabled(time)) {
                    return;
                }

                if (clearLeague) {
                    selectedLeagues.value = [];
                }

                dateFilterIndex.value = index;
                isSelectedLater.value = false;

                if (time === 'All') {
                    selectedDate.value = null;
                    return;
                }

                if (time === 'Later') {
                    const now = getNowWithGMT(-4);
                    now.setDate(now.getDate() + 5);
                    selectedDate.value = now;
                    isSelectedLater.value = true;
                    return;
                }

                selectedDate.value = time;
            }

            function changeTimeFilter(time: TimeFilter, index: number, clearLeague: boolean) {
                if (!isTimeFilterEnabled(time)) {
                    return;
                }

                if (clearLeague) {
                    selectedLeagues.value = [];
                }

                dateFilterIndex.value = index;

                if (time === 'All') {
                    selectedHour.value = null;
                    return;
                }

                selectedHour.value = time;
            }

            const leagueTimes = computed(() => props.leagueEvents.flat().filter((x): x is IMatch => (x.type !== 'outright')).map(x => x.showTime));

            function isDateFilterEnabled(time: DateFilterValue) {
                if (time === 'All') {
                    return true;
                }

                if (time === 'Later') {
                    const today = getNowWithGMT(-4);
                    const fiveDaysLater = addDay(today, 5);
                    return leagueTimes.value.some(x => new Date(x).getDate() >= fiveDaysLater.getDate());
                }

                return leagueTimes.value.some(x => new Date(x).getDate() === time.getDate());
            }

            function isTimeFilterEnabled(time: TimeFilter) {
                if (time === 'All') {
                    return true;
                }
                const now = getNowWithGMT(8);
                now.setHours(now.getHours() + time);
                return leagueTimes.value.some(x => new Date(x) <= now);
            }

            function dayTrackingClass(index: number) {
                switch (index) {
                    case 0:
                        return 'JARVIS_SportsPage_EarlyMarket_DateFilter_All';
                    case 4:
                        return 'JARVIS_SportsPage_EarlyMarket_DateFilter_LaterDates';
                    default:
                        return `JARVIS_SportsPage_EarlyMarket_DateFilter_${index}`;
                }
            }

            function timeTrackingClass(time: TimeFilter) {
                return time === 'All' ? 'JARVIS_SportsPage_Today_AllFilter' : `JARVIS_SportsPage_Today_Next${time}hrsFilter`;
            }

            const storeIndex = filter.value ? filter.value.dateFilterIndex : 0;
            if (isEarlyMarket.value) {
                changeDateFilter(dateFilterValues[storeIndex], storeIndex, false);
            } else if (isToday.value) {
                changeTimeFilter(timeFilters[storeIndex], storeIndex, false);
            }

            return {
                i18n,
                onClose,
                onClearAll,
                isShowDateFilter,
                dateFilterIndex,
                leagues,
                selectedLeagues,
                clickLeague,
                onSubmit,
                isEarlyMarket,
                dateFilters,
                dayTrackingClass,
                isDateFilterEnabled,
                dateFilterValues,
                changeDateFilter,
                changeTimeFilter,
                days,
                timeFilters,
                timeTrackingClass,
                isTimeFilterEnabled,
            };
        },
    });

</script>

<style lang="scss" scoped>
@import "@/scss/components/leagueFilter";
@import "@/scss/components/dateFilter";
</style>
